import { AUTH_COOKIE_HEADER } from '@/api/auth-cookie'
import { apiClient, getData } from '@/api/client'
import type { components } from '@/api/schema'
import {
  accountDeletedSchema,
  meSchema,
  memberProfileSchema,
  preferencesSchema,
  type ChangePasswordInput,
  type Language,
  type Me,
  type MemberProfile,
  type Plan,
  type Preferences,
} from '@/schemas/account'
import {
  meResponseSchema,
  passwordChangedSchema,
  type AuthSubscription,
  type AuthUser,
} from '@/schemas/auth'

/**
 * The seam between the app chrome and the backend, following
 * `features/auth/api/authService.ts`.
 *
 * What is real and what is not:
 *
 *   me()       REAL. GET /api/v5/auth/me/, mapped to the chrome's `Me`.
 *   profile()  REAL. `fullName` comes from the same /me call. Fields the
 *              backend does not publish are not represented in this boundary.
 *              When one ships, only the two constants below go away.
 *
 * The backend's vocabulary stops here. `tier`, `first_name` and friends are
 * mapped into the app's `Me` by the pure functions below, so the chrome, the
 * Profile and Subscription screens, and the sign-in flow that seeds the cache
 * all share one translation.
 */

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/**
 * Backend tier → the plan the chrome renders. The one mapping table.
 *
 * The backend sells Bull and Shark; the app's copy calls them Plus (Sadasya)
 * and Pro (Guru). Checked with `Object.hasOwn`, not `in` or a bare index, so
 * a tier that happens to be named like an Object.prototype member
 * ("constructor") cannot come back as a function.
 */
const PLAN_BY_TIER = {
  free: 'free',
  bull: 'plus',
  shark: 'pro',
} as const satisfies Record<string, Plan>

/**
 * An unknown tier maps to free, not to an error: failing closed means a tier
 * added on the backend before this build knows it shows as Free — the member
 * sees an upsell they may not need — rather than breaking the chrome on every
 * route. Entitlement is enforced server-side either way (SUBSCRIPTION_REQUIRED),
 * so this is presentation, never a gate.
 */
export const planFromTier = (tier: string): Plan =>
  Object.hasOwn(PLAN_BY_TIER, tier) ? PLAN_BY_TIER[tier as keyof typeof PLAN_BY_TIER] : 'free'

/**
 * What to call the member in the chrome ("Namaste, Sita!").
 *
 * First name first. It can be blank — the backend splits "Full name" on
 * whitespace, and accounts created before that (or by social sign-in) may
 * have no name at all — so fall back through the other things that identify
 * the account: last name, then email, then phone. An email in the greeting
 * reads oddly, but a sidebar card that names someone else's account would be
 * worse than one that names this one plainly. `Member` is the TopNav's own
 * fallback, used only if the backend sent nothing at all.
 */
export const displayNameFrom = (user: AuthUser): string =>
  [user.first_name, user.last_name, user.email, user.phone]
    .map((v) => v?.trim() ?? '')
    .find((v) => v.length > 0) ?? 'Member'

/** "Sita Sharma", or the display name when there is no first/last name. */
export const fullNameFrom = (user: AuthUser): string =>
  [user.first_name.trim(), user.last_name.trim()].filter(Boolean).join(' ') || displayNameFrom(user)

/**
 * The chrome's identity from the user and subscription the backend sends.
 *
 * Shared by `me()` and by sign-in: the login response carries the same two
 * objects, so the auth flow seeds `accountKeys.me()` with this and Home paints
 * the member's name without a second round trip.
 *
 * `avatar` is always null — the backend stores no avatar, and null is the
 * chrome's "use initials" signal.
 */
export const meFromAuth = (user: AuthUser, subscription: AuthSubscription): Me =>
  meSchema.parse({
    name: displayNameFrom(user),
    fullName: fullNameFrom(user),
    plan: planFromTier(subscription.tier),
    avatar: null,
  })

/**
 * "Sita Kumari Sharma" → first "Sita", last "Kumari Sharma": the one Full name field the page
 * shows over the two the backend stores. Null when there is no second word, which mm-be refuses
 * (`last_name` may not be blank).
 */
export const splitFullName = (
  fullName: string,
): { first_name: string; last_name: string } | null => {
  const words = fullName.trim().split(/\s+/).filter(Boolean)
  if (words.length < 2) return null
  const [first, ...rest] = words
  return first === undefined ? null : { first_name: first, last_name: rest.join(' ') }
}

const profileFromUser = (user: AuthUser, hasPassword: boolean): MemberProfile =>
  memberProfileSchema.parse({
    fullName: fullNameFrom(user),
    // Straight off the wire, not trimmed to a display string: the Account
    // hub prints them as the member typed them, and Edit profile seeds its
    // fields from the same values.
    phone: user.phone,
    email: user.email,
    phoneVerified: user.otp_verified,
    hasPassword,
  })

const fetchMe = async () => {
  const data = await getData<unknown>(() => apiClient.GET('/api/v5/auth/me/'))
  return meResponseSchema.parse(data) satisfies Wire<'MeResponse'>
}

export const accountService = {
  me: async (): Promise<Me> => {
    const { user, subscription } = await fetchMe()
    return meFromAuth(user, subscription)
  },

  /**
   * Its own /me request rather than a read of the `me` cache: a service has
   * no QueryClient, and the Profile screen is the only caller, so the cost is
   * one extra request per session on one screen.
   */
  profile: async (): Promise<MemberProfile> => {
    const { user, has_password } = await fetchMe()
    return profileFromUser(user, has_password)
  },

  /**
   * PATCH /auth/me/ — the rename (C1). mm-be answers with the whole MeResponse, so both the
   * chrome's `Me` and the Account `MemberProfile` come back from one call and the caller seeds
   * both caches without a refetch.
   */
  rename: async (name: { first_name: string; last_name: string }) => {
    const data = await getData<unknown>(() => apiClient.PATCH('/api/v5/auth/me/', { body: name }))
    const { user, subscription, has_password } = meResponseSchema.parse(data)
    return { me: meFromAuth(user, subscription), profile: profileFromUser(user, has_password) }
  },

  /** GET /auth/preferences/: today only the language company summaries are served in. */
  preferences: async (): Promise<Preferences> =>
    preferencesSchema.parse(
      await getData<unknown>(() => apiClient.GET('/api/v5/auth/preferences/')),
    ),

  setLanguage: async (language: Language): Promise<Preferences> =>
    preferencesSchema.parse(
      await getData<unknown>(() =>
        apiClient.PATCH('/api/v5/auth/preferences/', { body: { language } }),
      ),
    ),

  /**
   * POST /auth/password/change/ — confirmed with the current password. mm-be revokes every other
   * device's refresh session and keeps this one, so nothing comes back to store. `again` is sent
   * as typed, so mm-be's own match check is a real second look rather than a copy of `next`.
   */
  changePassword: async ({ current, next, again }: ChangePasswordInput): Promise<void> => {
    const data = await getData<unknown>(() =>
      apiClient.POST('/api/v5/auth/password/change/', {
        body: { old_password: current, new_password: next, confirm_password: again },
      }),
    )
    passwordChangedSchema.parse(data) satisfies Wire<'PasswordChangedResponse'>
  },

  /**
   * POST /auth/account/delete/ — confirmed with the password (AC8: mm-be has no SMS-code
   * delete). In cookie mode, so mm-be clears the refresh cookie with the session it revokes: a
   * new tab must not sign back into a closed account.
   */
  deleteAccount: async (password: string): Promise<void> => {
    accountDeletedSchema.parse(
      await getData<unknown>(() =>
        apiClient.POST('/api/v5/auth/account/delete/', {
          body: { password, reason: '' },
          params: { header: { [AUTH_COOKIE_HEADER]: '1' } },
          credentials: 'include',
        }),
      ),
    )
  },
}
